import { lazy, Suspense } from 'react';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import DashboardLayout from 'src/layouts/dashboard';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { IssueLogSearchParamsSchema } from 'src/sections/issue-log/view';

import { rootRoute } from './root';
import { ordersRoute } from './orders';

const IssueLogList = lazy(() => import('src/sections/issue-log/view'));
const IssueDetails = lazy(() => import('src/sections/issue-log/issue-details/view'));
const OrderIssueAnonymousReplyDetails = lazy(
  () => import('src/sections/issue-log/issue-details/anonymous-view')
);
const AnonymousOrderIssueSuccess = lazy(
  () => import('src/sections/issue-log/issue-details/anonymous-success-view')
);

type AnonymousReplySearch = {
  accept?: boolean;
};

// Issues routes
const issuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'issues',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <DashboardLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </AppAuthGuard>
  ),
});

export const issueDetailsRoute = createRoute({
  getParentRoute: () => ordersRoute,
  path: '$orderId/issues/$issueId',
  component: IssueDetails,
});

const issuesIndexRoute = createRoute({
  getParentRoute: () => issuesRoute,
  path: '/',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    IssueLogSearchParamsSchema.validateSync(search),
  component: IssueLogList,
});

// Anonymous routes (no auth required)
const issuesAnonymousRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'order-issues-email',
  component: () => (
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  ),
  validateSearch: (search: Record<string, unknown>): AnonymousReplySearch => ({
    accept: search.accept === true || search.accept === 'true' ? true : undefined,
  }),
});

const orderIssueAnonymousReplyRoute = createRoute({
  getParentRoute: () => issuesAnonymousRoute,
  path: '/$emailReplyId',
  component: OrderIssueAnonymousReplyDetails,
});

const orderIssueAnonymousReplySuccessRoute = createRoute({
  getParentRoute: () => issuesAnonymousRoute,
  path: '/success',
  component: AnonymousOrderIssueSuccess,
});

export const issuesRouteTree = issuesRoute.addChildren([issuesIndexRoute]);

export const issuesAnonymousRouteTree = issuesAnonymousRoute.addChildren([
  orderIssueAnonymousReplyRoute,
  orderIssueAnonymousReplySuccessRoute,
]);
